import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { apiCall } from '../lib/api';
import { trackEvent } from '../lib/analytics';
import type { ProductListOutput, Collection } from '@meso/types';

export function Products() {
  const [params, setParams] = useSearchParams();
  const collectionHandle = params.get('collection') || '';

  useEffect(() => { trackEvent('view_item_list', { collection: collectionHandle }); }, [collectionHandle]);

  // Collections (chips)
  const collections = useQuery<{ collections: Collection[] }>({
    queryKey: ['collections'],
    queryFn: () => apiCall<{ collections: Collection[] }>('get', '/shop/collections'),
    staleTime: 5 * 60_000,
  });

  // Products (paginated)
  const products = useInfiniteQuery({
    queryKey: ['products', collectionHandle],
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams({ limit: '24' });
      if (collectionHandle) qs.set('collection', collectionHandle);
      if (pageParam) qs.set('cursor', String(pageParam));
      return apiCall<ProductListOutput>('get', `/shop/products?${qs.toString()}`);
    },
    initialPageParam: '' as string,
    getNextPageParam: (last) =>
      last.pageInfo.hasNextPage ? last.pageInfo.endCursor ?? undefined : undefined,
  });

  const allProducts = products.data?.pages.flatMap((p) => p.products) ?? [];
  const collectionTitle = products.data?.pages[0]?.collectionTitle;

  function selectCollection(handle: string) {
    if (handle) setParams({ collection: handle });
    else setParams({});
  }

  return (
    <div className="max-w-md mx-auto px-4 py-4">
      <h1 className="text-xl font-bold mb-3">{collectionTitle ?? 'สินค้า'}</h1>

      {/* Collection chips */}
      {collections.data?.collections && collections.data.collections.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-3 mb-2 -mx-4 px-4">
          <button
            onClick={() => selectCollection('')}
            className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm border flex-shrink-0 ${
              !collectionHandle
                ? 'bg-black text-white border-black'
                : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            ทั้งหมด
          </button>
          {collections.data.collections.map((col) => (
            <button
              key={col.id}
              onClick={() => selectCollection(col.handle)}
              className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm border flex-shrink-0 ${
                collectionHandle === col.handle
                  ? 'bg-black text-white border-black'
                  : 'bg-white text-gray-700 border-gray-300'
              }`}
            >
              {col.title}
            </button>
          ))}
        </div>
      )}

      {/* Loading */}
      {products.isLoading && (
        <div className="p-6 text-center text-gray-500">กำลังโหลดสินค้า...</div>
      )}

      {/* Error */}
      {products.isError && (
        <div className="card text-red-500 text-sm">
          โหลดสินค้าไม่ได้: {(products.error as Error).message}
        </div>
      )}

      {/* Empty state */}
      {!products.isLoading && allProducts.length === 0 && (
        <div className="card text-center py-12 text-gray-500">
          ไม่มีสินค้าในหมวดนี้
        </div>
      )}

      {/* Product grid */}
      {allProducts.length > 0 && (
        <>
          <div className="grid grid-cols-2 gap-3">
            {allProducts.map((p) => (
              <Link
                key={p.id}
                to={`/shop/products/${p.handle}`}
                className={`card p-2 active:scale-95 transition ${!p.available ? 'opacity-60' : ''}`}
              >
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden mb-2 relative">
                  {p.featuredImage ? (
                    <img src={p.featuredImage.url} alt={p.featuredImage.altText ?? ''}
                         className={`w-full h-full object-cover ${!p.available ? 'grayscale' : ''}`}
                         loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300 text-3xl">📦</div>
                  )}
                  {!p.available && (
                    <div className="absolute bottom-0 left-0 right-0 bg-black/55 text-white text-xs font-bold text-center py-1.5">
                      สินค้าหมด
                    </div>
                  )}
                </div>
                <div className="text-xs font-semibold line-clamp-2 min-h-[2.5em]">{p.title}</div>
                <div className="text-sm font-bold mt-1">
                  {p.priceRange.min.amount === p.priceRange.max.amount
                    ? `฿${parseFloat(p.priceRange.min.amount).toLocaleString('th-TH')}`
                    : `฿${parseFloat(p.priceRange.min.amount).toLocaleString('th-TH')} - ฿${parseFloat(p.priceRange.max.amount).toLocaleString('th-TH')}`}
                </div>
              </Link>
            ))}
          </div>

          {/* Load more */}
          {products.hasNextPage && (
            <button
              onClick={() => products.fetchNextPage()}
              disabled={products.isFetchingNextPage}
              className="btn btn-outline btn-block mt-4"
            >
              {products.isFetchingNextPage ? 'กำลังโหลด...' : 'โหลดเพิ่มเติม'}
            </button>
          )}

          <div className="text-center text-xs text-gray-400 mt-3">
            {allProducts.length} รายการ {!products.hasNextPage && '(ครบแล้ว)'}
          </div>
        </>
      )}
    </div>
  );
}
